// AdX - centralized site content.
// Business facts are drawn strictly from the concept note.
// EDITABLE PLACEHOLDERS (network size, live counts, testimonials) use [brackets].

export const APP_URL = "https://app.adx.vc";
export const REGISTER_URL = "https://app.adx.vc/register";

export const BRAND = {
  name: "AdX",
  full: "AdX",
  domain: "adx.vc",
  parent: "SevenM Technologies",
  tagline: "Where Communities Meet Brands.",
  subline:
    "Technology-driven residential digital advertising - connecting brands with residents through digital screens across housing societies.",
  // Editable owner/contact placeholders
  email: "hello@adx.vc",
  phone: "+91 33 6902 9191",
  address: "Kolkata, India",
  offices: [
    { city: "Kolkata", address: "508, 5th Floor, Saltee Plaza, 1 Khudiram Bose Sarani, Kolkata 700080" },
    { city: "Bengaluru", address: "8th Floor, The Estate, Dickenson Road, Bengaluru 560042" },
    { city: "UAE", address: "SevenM Digital FZE, BC 1306321, C1, Ajman Free Zone Building, UAE" },
  ],
};

export const NAV = [
  { label: "For Societies", to: "/societies" },
  { label: "For Commercial", to: "/commercial" },
  { label: "For Advertisers", to: "/advertisers" },
  { label: "Platform", to: "/platform" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
  { label: "Insights", to: "/blog" },
];

// Live network metrics - update monthly.
export const METRICS = [
  { label: "Screens deployed", value: "900+", note: "Live network count" },
  { label: "Societies partnered", value: "180+", note: "And growing" },
  { label: "Cities", value: "9+", note: "Across India" },
  { label: "Impressions delivered", value: "18Mn", note: "Across the network" },
];

export const PROBLEMS = [
  {
    icon: "PiggyBank",
    title: "Societies earn ₹800/month while platforms pocket the rest.",
    detail: "Token fixed rents against inventory that runs hundreds of ad slots a day.",
  },
  {
    icon: "Lock",
    title: "Locked into 5-year contracts with no way out.",
    detail: "Auto-renewal, non-compete clauses, and platform-appointed arbitrators.",
  },
  {
    icon: "PhoneOff",
    title: "Advertisers can't book a campaign without calling a salesperson.",
    detail: "No self-serve platform. No published rate card. No live dashboards.",
  },
];

export const AUDIENCES = [
  {
    key: "societies",
    label: "For Societies",
    to: "/societies",
    headline: "Earn more. See everything.",
    points: [
      "Transparent, slab-based monthly payouts",
      "Live screen-health dashboard - real transparency",
      "Fair 3-year contracts. No lock-in. No non-compete.",
      "We pay for everything - including electricity",
    ],
  },
  {
    key: "advertisers",
    label: "For Advertisers",
    to: "/advertisers",
    headline: "Book in minutes, not weeks.",
    points: [
      "Self-serve platform - go live fast",
      "Pay per impression - transparent rates, no negotiation",
      "Start with a single 5-second ad",
      "Prepaid wallet, real-time tracking, playback evidence",
    ],
  },
  {
    key: "residents",
    label: "For Residents",
    to: "/platform",
    headline: "Screens worth your attention.",
    points: [
      "Protected community content every 5 minutes",
      "Smart notice board - live from the next cycle",
      "Emergency broadcast for urgent messages",
      "Local business highlights, never blank screens",
    ],
  },
];

export const HOW_SOCIETIES = [
  { n: "01", title: "We assess your community", body: "Tower count, elevator lobbies, footfall and demographics - then a transparent proposal with no hidden clauses." },
  { n: "02", title: "We install screens at zero cost", body: "32″ portrait LED screens, cabling, connectivity, insurance and maintenance - all covered by AdX." },
  { n: "03", title: "Ads play - you earn every month", body: "Screens run 06:00 AM–11:30 PM. Fixed rent credited by the 10th of the following month." },
  { n: "04", title: "Track everything on your dashboard", body: "Screen health at a glance, community content manager, and emergency override - all self-serve." },
];

export const HOW_ADVERTISERS = [
  { n: "01", title: "Sign up and load your wallet", body: "Register on the self-serve platform at app.adx.vc and top up your prepaid wallet. Pay for exactly what you use." },
  { n: "02", title: "Choose societies, length & frequency", body: "Pick societies by city or name, an ad length of 5–30s, and how often it plays - every 1, 2, 3 or 5 minutes." },
  { n: "03", title: "Upload your creative", body: "Image or video that exactly matches your chosen duration. The system validates automatically - no trimming, no surprises." },
  { n: "04", title: "Go live within 24 hours", body: "Creative reviewed by AdX, then live at 06:00 AM the next day. Track impressions, spend and balance in real time." },
];

export const DIFFERENCE = [
  { adx: "Transparent slab-based payout", old: "Opaque, fixed rents", label: "Monthly society payout" },
  { adx: "3-year fair terms", old: "5-year lock-in", label: "Contract" },
  { adx: "Self-serve booking", old: "Call a salesperson", label: "Advertiser access" },
  { adx: "Per-impression billing", old: "Opaque invoices", label: "Billing" },
];

// Full comparison table (For Societies + Platform pages)
export const COMPARISON = [
  { dim: "Society payout", old: "Opaque, fixed token rents", adx: "Transparent, slab-based payouts" },
  { dim: "Revenue transparency", old: "None", adx: "Live dashboard: screen health & ad activity" },
  { dim: "Contract duration", old: "5 years, auto-renewable", adx: "3 years, renewal by mutual agreement" },
  { dim: "Exit clause", old: "30-day notice before auto-renewal", adx: "90-day exit for either party, with cause" },
  { dim: "Non-compete", old: "During and after the contract", adx: "None - you're free at contract end" },
  { dim: "Arbitration", old: "Platform-appointed arbitrator", adx: "Mutually appointed" },
  { dim: "Electricity", old: "Society bears cost (some contracts)", adx: "Covered by AdX" },
  { dim: "Community content", old: "Restricted; no local business promo", adx: "Upload freely; local promo welcome" },
  { dim: "Emergency override", old: "Must request from platform", adx: "Immediate override for the society" },
  { dim: "Advertiser platform", old: "Manual sales, opaque pricing", adx: "Self-serve, published rate card" },
  { dim: "Reporting evidence", old: "Post-campaign PDF", adx: "Detailed playback records" },
  { dim: "Missed impressions", old: "No compensation", adx: "Auto compensation or wallet credit" },
  { dim: "Screen monitoring", old: "Reactive maintenance", adx: "Real-time heartbeat, 3-min detection" },
];

export const SOCIETY_BENEFITS = [
  { icon: "Coins", title: "Transparent Rent", body: "Slab-based rent with electricity included - clear terms, finalized per community." },
  { icon: "Wrench", title: "Zero-Cost Installation", body: "Hardware, internet, insurance, maintenance and electricity - all covered by AdX." },
  { icon: "LayoutDashboard", title: "Community Dashboard", body: "Upload notices, view screen health, and manage content - no dependency on our team." },
  { icon: "Megaphone", title: "Smart Notice Board", body: "Push text, image or video notices - live from the very next 5-minute cycle." },
  { icon: "Siren", title: "Emergency Override", body: "Request an immediate full-screen takeover for urgent community messages." },
  { icon: "Activity", title: "Screen-Health Visibility", body: "See how many screens are operational at a glance, e.g. 5 of 6 online." },
  { icon: "RefreshCw", title: "Annual Upgrades", body: "Free hardware and software upgrades throughout the partnership." },
  { icon: "ShieldCheck", title: "Protected Community Block", body: "A 15-second slot every 5 minutes that can never be sold to advertisers - now carrying live weather & AQI for residents, with news and cricket scores following." },
];

export const AGREEMENT_CALLOUTS = [
  "3 years, not 5",
  "No auto-renewal - renewal is your choice",
  "90-day exit clause - for either party",
  "Mutual arbitrator - not theirs alone",
  "No non-compete - free when the contract ends",
  "We pay electricity - included in our model",
  "We cover install, connectivity, maintenance & insurance",
];

export const SOCIETY_FAQ = [
  { q: "Who pays for installation?", a: "AdX - at zero cost to you. Hardware, cabling and connectivity are all covered." },
  { q: "What if a screen gets damaged?", a: "Every device is insured and remotely monitored. We handle repairs and replacements." },
  { q: "Can we use screens for our own content?", a: "Yes. You get a protected community block every 5 minutes and a dashboard to manage it." },
  { q: "What internet do we need?", a: "None. We provide 4G/5G connectivity as part of the installation." },
  { q: "How quickly can we go live?", a: "Typically 7–10 days from signing the agreement." },
  { q: "What happens when the contract ends?", a: "We remove the equipment. No obligations, no non-compete - you're completely free." },
  { q: "Who pays for electricity?", a: "AdX covers it. It's built into our model." },
  { q: "Can we see what ads are playing?", a: "Yes - screen health and ad activity are visible on your community dashboard." },
];

export const SOCIETY_SLABS = {
  note: "Rent per screen, per month - excluding electricity.",
  cols: ["<70% Occupied", "70–90% Occupied", ">90% Occupied"],
  rows: [
    { label: "<300 flats", values: [600, 700, 800] },
    { label: "300–500 flats", values: [700, 900, 1000] },
    { label: ">500 flats", values: [900, 1000, 1200] },
  ],
  electricity: 300,
  totalMin: 900,
  totalMax: 1500,
};

export const ADVERTISER_FAQ = [
  { q: "How do I book a campaign?", a: "Register at app.adx.vc, load your prepaid wallet, choose your societies, ad length and frequency, upload your creative, and go live after a quick review." },
  { q: "What ad lengths and frequencies can I choose?", a: "Ad lengths of 5 to 30 seconds (in 5-second steps), played every 1, 2, 3 or 5 minutes." },
  { q: "How is pricing calculated?", a: "Transparent per-impression pricing starting at 1p per second. Your final cost depends on ad length, frequency, screens, societies and campaign duration. Plays inside prime-time bands (07:00–10:00 and 18:00–22:00) carry a 25% premium on the base rate." },
  { q: "What are prime-time hours?", a: "07:00–10:00 and 18:00–22:00, when elevator lobbies are at their busiest. Plays in these bands are billed at 1.25p per second; all other hours use the standard 1p rate." },
  { q: "Do you offer discounts?", a: "Yes - through coupon codes, never through negotiation. Registered advertisers receive codes from time to time based on campaign duration, frequency commitments and festive calendars. The rate card itself is identical for everyone." },
  { q: "What counts as an impression?", a: "An impression is counted when your advertisement plays on an operational screen." },
  { q: "Is there a minimum spend?", a: "No minimum beyond your wallet balance. Campaigns are fully prepaid, so you're always in control." },
  { q: "What if my wallet balance runs low?", a: "Your campaign automatically pauses. Recharge your wallet and resume whenever you're ready." },
  { q: "What creative formats are supported?", a: "Image or video that matches your selected ad duration. Every creative is reviewed before it goes live." },
  { q: "When does my campaign start?", a: "Approved campaigns are scheduled through the AdX workflow and begin at 06:00 AM." },
];

export const WHY_DOOH = [
  { stat: "6×", label: "daily viewing frequency", note: "Residents pass lobbies 4–8 times a day" },
  { stat: "~30s", label: "average dwell time", note: "Waiting for the elevator, eyes on screen" },
  { stat: "100%", label: "field of view", note: "Close-proximity 32″ screens dominate attention" },
  { stat: "Zero", label: "clutter", note: "One advertiser plays at a time, per society" },
  { stat: "Premium", label: "audiences", note: "Gated societies, high household income" },
];

export const LENGTH_MULTIPLIERS = [
  { len: "5 sec", slots: "1 slot" },
  { len: "10 sec", slots: "2 slots" },
  { len: "15 sec", slots: "3 slots" },
  { len: "20 sec", slots: "4 slots" },
  { len: "25 sec", slots: "5 slots" },
  { len: "30 sec", slots: "6 slots" },
];

export const FREQ_MULTIPLIERS = [
  { freq: "Every 1 min", desc: "Maximum Exposure" },
  { freq: "Every 2 min", desc: "High Exposure" },
  { freq: "Every 3 min", desc: "Balanced" },
  { freq: "Every 5 min", desc: "Light Touch" },
];

// Numeric maps for the interactive calculator. Anchor: ₹0.01 per second, per screen impression.
export const BASE_RATE = 0.01;
export const LENGTH_MAP = { 5: 1.0, 10: 1.8, 15: 2.5, 20: 3.1, 25: 3.6, 30: 4.0 };
export const FREQ_MAP = { 1: 1.4, 2: 1.2, 3: 1.0, 5: 0.85 };

export const CAMPAIGN_LIFECYCLE = [
  { n: "01", t: "Create campaign", d: "Select societies, ad length, frequency and duration." },
  { n: "02", t: "Upload creative", d: "Image or video that must exactly match your chosen duration - validated automatically." },
  { n: "03", t: "Review by AdX", d: "Creative reviewed and approved, typically within 24 hours." },
  { n: "04", t: "Go live", d: "Starts at 06:00 AM the day after 24 hours from approval." },
  { n: "05", t: "Track in real time", d: "Impressions delivered, spend and wallet balance - live." },
  { n: "06", t: "Full campaign report", d: "Detailed campaign records available when the campaign ends." },
];

export const PLATFORM_LAYERS = [
  {
    n: "01",
    name: "Screen OS",
    tag: "Device Layer",
    icon: "Cpu",
    points: [
      "Custom Android-based firmware with remote content push",
      "Heartbeat every 30 seconds - 3-minute offline detection",
      "Offline cache for continuity, OTA firmware updates",
      "Remote restart, reboot and force-sync controls",
    ],
  },
  {
    n: "02",
    name: "Content Engine",
    tag: "Scheduling Layer",
    icon: "Layers",
    points: [
      "5-minute master-cycle scheduling engine",
      "Continuous playback, one advertiser at a time",
      "Frequency enforcement within contracted intervals",
      "Protected 15-second community block every cycle",
    ],
  },
  {
    n: "03",
    name: "Marketplace",
    tag: "Transaction Layer",
    icon: "Store",
    points: [
      "Self-serve campaign builder and society selector",
      "Transparent pricing engine with published multipliers",
      "Prepaid wallet with real-time balance and auto-pause",
      "Per-impression billing with a full audit trail",
    ],
  },
  {
    n: "04",
    name: "Community Dashboard",
    tag: "Society Layer",
    icon: "Home",
    points: [
      "Instant notice upload - text, image or video",
      "Emergency broadcast override",
      "Aggregate screen-health overview",
      "One active community message, changeable anytime",
    ],
  },
];

export const TECH_COMPARISON = [
  { cap: "Advertiser booking", old: "Manual (email / call)", adx: "Self-serve - book in minutes" },
  { cap: "Pricing", old: "Opaque, negotiated", adx: "Published rate card, per-impression" },
  { cap: "Payment", old: "Invoice / negotiated", adx: "Prepaid wallet, real-time balance" },
  { cap: "Creative validation", old: "Manual review only", adx: "Automated duration match + manual review" },
  { cap: "Reporting evidence", old: "Post-campaign PDF", adx: "Detailed playback records" },
  { cap: "Missed impressions", old: "No compensation", adx: "Campaign extension or wallet credit" },
  { cap: "Analytics", old: "Post-campaign PDF", adx: "Real-time dashboard, live metrics" },
  { cap: "Screen monitoring", old: "Reactive maintenance", adx: "Heartbeat every 30 seconds" },
];

export const ROADMAP = [
  "AI-driven content scheduling & dynamic creative optimization",
  "Programmatic SSP - OpenRTB integration for DSPs",
  "AI Creative Studio - broadcast-ready 9:16 creatives from brand assets",
  "Live community feeds - news & cricket scores",
  "A/B testing with auto-optimization",
  "Conversion tracking via dynamic QR codes",
];

export const VALUES = [
  { t: "Transparency over opacity", d: "Numbers, dashboards and screen health are always visible." },
  { t: "Fairness over lock-in", d: "Contracts a society committee reads and feels good about signing." },
  { t: "Technology over tradition", d: "A full-stack platform, not a relationship-driven media desk." },
  { t: "Community over commerce", d: "Protected community time on every single screen, every cycle." },
];

export const WHY_WINS = [
  "Transparent, slab-based payouts",
  "Live screen-health dashboard",
  "Fair 3-year contracts, no lock-in",
  "No non-compete, ever",
  "Zero cost to the society",
  "Protected community time",
  "Self-serve ad booking",
  "Transparent per-impression pricing",
  "Playback evidence & reporting",
  "Fair delivery compensation",
];

export const COMMERCIAL_BENEFITS = [
  { icon: "Coins", title: "Rent for dead lobby space", body: "Your elevator lobby earns a fixed monthly rent - space that generated nothing now pays for itself." },
  { icon: "Wrench", title: "Zero cost, zero hassle", body: "We supply, install, insure and maintain every screen. Your facility team lifts no finger." },
  { icon: "LayoutDashboard", title: "Live screen dashboard", body: "Building management sees online/offline status of every screen in real time." },
  { icon: "Megaphone", title: "Tenant & visitor notices", body: "A protected community block in every cycle carries building announcements - plus live weather & AQI." },
  { icon: "Siren", title: "Content you control", body: "Office-appropriate, non-intrusive advertising. No political, adult or competitor-ambush content." },
  { icon: "Activity", title: "Remote health monitoring", body: "Offline screens are detected within 3 minutes and serviced by us - not your staff." },
  { icon: "RefreshCw", title: "Clean exit terms", body: "Clear agreement with defined notice. No lock-in traps, no surprise clauses." },
  { icon: "ShieldCheck", title: "Fully insured hardware", body: "Every screen is insured. Any damage is on us - documented in the agreement." },
];

export const COMMERCIAL_STEPS = [
  { n: "01", title: "Share your building details", body: "Two minutes - locality, floors, daily footfall. We assess fit and get back fast." },
  { n: "02", title: "Site survey & proposal", body: "We visit, map lobby placements, and share a transparent proposal with rent and terms." },
  { n: "03", title: "Ads play - you earn every month", body: "Screens run 06:00 AM–11:30 PM. Fixed rent credited by the 10th of the following month." },
];

export const COMMERCIAL_FAQ = [
  { q: "Does it cost the building anything?", a: "No. Screens, installation, insurance, maintenance and electricity consumption (~40W per screen) are entirely on AdX. The building receives a fixed monthly rent." },
  { q: "Who is the audience?", a: "Office-goers, tenants and visitors passing through your elevator lobbies - one of the most valuable professional audiences for advertisers, multiple times a day." },
  { q: "How is the rent calculated?", a: "Rent is structured by lobby count, floors and daily footfall, with electricity included. Indicative terms are shared in the proposal; final commercial terms are confirmed per building." },
  { q: "What content plays on the screens?", a: "Brand advertising in office-appropriate categories, a protected community block for building notices, and live weather & AQI - with news and cricket scores following shortly." },
  { q: "Will it clutter our lobby?", a: "No. Custom casing, concealed wiring and flush-mount options - installed to respect your lobby's design, with sign-off from your facility team before we drill a single hole." },
  { q: "What if we want to exit?", a: "The agreement carries a clear notice period and defined removal process. No lock-in traps." },
];

export const SEO = {
  home: {
    title: "AdX - Technology-Driven Residential DOOH Advertising",
    description:
      "AdX is a technology-driven digital advertising platform for residential communities, connecting brands with residents through digital screens across housing societies. Transparent, prepaid, per-impression advertising.",
  },
  societies: {
    title: "Partner Your Society with AdX - Earn from Elevator Lobby Screens",
    description:
      "AdX installs digital screens at zero upfront cost to the society and shares a transparent, slab-based monthly payout with electricity included. Clear 3-year terms, confirmed per community. Live dashboard.",
  },
  commercial: {
    title: "Partner Your Commercial Building with AdX - Earn from Office Lobby Screens",
    description:
      "AdX installs premium digital screens in commercial and office building lobbies at zero cost, pays a fixed monthly rent, and runs office-appropriate advertising for a professional audience. Fully insured, remotely monitored.",
  },
  advertisers: {
    title: "Advertise on Residential Screens - Self-Serve DOOH Platform | AdX",
    description:
      "Reach premium households on elevator lobby screens. Transparent per-impression pricing from 1 paisa per second. Prepaid wallet. Playback evidence. Self-serve booking. 5–30 second ads, flexible frequency.",
  },
  platform: {
    title: "DOOH Technology Platform - Real-Time Monitoring & Self-Serve Marketplace | AdX",
    description:
      "Full-stack DOOH platform: automated screen monitoring, self-serve campaign booking, per-impression billing, detailed campaign reporting, prepaid wallet system.",
  },
  pricing: {
    title: "Advertiser Pricing - Transparent Per-Impression Rates | AdX",
    description:
      "Transparent, prepaid advertising for residential communities. Pricing starts at 1 paisa per second. Choose ad length (5–30s) and frequency (1–5 min). Prepaid wallet, playback evidence.",
  },
  about: {
    title: "About AdX - A SevenM Technologies Venture",
    description:
      "AdX is a DOOH advertising platform and a venture of SevenM Technologies. We built AdX to give societies and advertisers a fairer, more transparent deal.",
  },
  blog: {
    title: "Insights - AdX",
    description: "Thinking on fair DOOH, transparent pricing, and the future of residential advertising.",
  },
  contact: {
    title: "Get Started with AdX - Partner Your Society or Advertise",
    description: "Get a proposal for your community, or start your first campaign on AdX - a transparent, technology-driven residential DOOH platform.",
  },
};

export const IMAGES = {
  heroLobby:
    "/hero-square.png",
  digitalScreen:
    "https://images.unsplash.com/photo-1533069027836-fa937181a8ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwYmlsbGJvYXJkfGVufDB8fHx8MTc4NzgwMTE5NHww&ixlib=rb-4.1.0&q=85",
  networkCity:
    "https://images.unsplash.com/photo-1664353655151-9d94a9170eb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxjaXR5JTIwc2t5bGluZSUyMG5pZ2h0fGVufDB8fHx8MTc4NzgwMTE5NHww&ixlib=rb-4.1.0&q=85",
  audienceLobby:
    "https://images.unsplash.com/photo-1758800601486-75c3865cc9a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBsb2JieXxlbnwwfHx8fDE3ODc4MDExOTV8MA&ixlib=rb-4.1.0&q=85",
};
